import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Leaf, Download, Upload, Printer, FileSpreadsheet, PackageOpen, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { productsAPI, categoriesAPI, clientCategoriesAPI, csvAPI, adminAPI } from '../lib/api';
import { toast } from 'sonner';
import { printContent, generateProductsListHTML } from '../lib/print';
import { Pagination } from '../components/Pagination';
import { useBulkSelect } from '../hooks/useBulkSelect';
import { ProductFilters } from '../components/admin/products/ProductFilters';
import { ProductBulkActions } from '../components/admin/products/ProductBulkActions';
import { ProductImportHistory } from '../components/admin/products/ProductImportHistory';
import { ProductTable } from '../components/admin/products/ProductTable';
import { ProductForm } from '../components/admin/products/ProductForm';
import { PriceImportPDF } from '../components/admin/products/PriceImportPDF';

const PRODUCTS_PER_PAGE = 50;

export const AdminProducts = () => {
    const fileInputRef = useRef(null);
    const supplierFileRef = useRef(null);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [clientCategories, setClientCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalProducts, setTotalProducts] = useState(0);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [productToDelete, setProductToDelete] = useState(null);
    const [editingProduct, setEditingProduct] = useState(null);
    const [showPricing, setShowPricing] = useState(false);
    const [allSuppliers, setAllSuppliers] = useState([]);
    const [productSuppliers, setProductSuppliers] = useState([]);
    const { selectedIds, selectedCount, toggleItem, toggleAll, isSelected, allSelected, deselectAll } = useBulkSelect(products);
    const [importHistory, setImportHistory] = useState([]);
    const [showImportHistory, setShowImportHistory] = useState(false);
    const [pricePdfOpen, setPricePdfOpen] = useState(false);
    const isSuperAdmin = (() => {
        try { const u = JSON.parse(localStorage.getItem('prixdeal_user') || '{}'); return u.is_super_admin === true; }
        catch { return false; }
    })();
    const [formData, setFormData] = useState({
        name: '', description: '', price: '', unit: 'kg', min_order: 1, category_id: '',
        image_url: '', origin: '', stock: 100, product_code: '', gen_code: '',
        package_weight: '', purchase_price: '', calibre: '',
        prices_by_category: [], is_weekly_offer: false, season_start: '', season_end: '',
    });

    useEffect(() => { loadData(); }, [currentPage, searchQuery, categoryFilter, statusFilter]);

    useEffect(() => {
        const timer = setTimeout(() => { setSearchQuery(search); setCurrentPage(1); }, 300);
        return () => clearTimeout(timer);
    }, [search]);

    const loadData = async () => {
        try {
            const skip = (currentPage - 1) * PRODUCTS_PER_PAGE;
            const params = { skip, limit: PRODUCTS_PER_PAGE };
            if (searchQuery) params.search = searchQuery;
            if (categoryFilter !== 'all') params.category = categoryFilter;
            if (statusFilter !== 'all') params.status = statusFilter;
            const [prodRes, catRes, clientCatRes, suppRes] = await Promise.all([
                productsAPI.getAllAdmin(params), categoriesAPI.getAll(), clientCategoriesAPI.getAll(), adminAPI.getSuppliers()
            ]);
            setProducts(prodRes.data.products);
            setTotalProducts(prodRes.data.total || 0);
            setCategories(catRes.data.categories || []);
            setClientCategories(clientCatRes.data.categories || clientCatRes.data || []);
            setAllSuppliers(suppRes.data.suppliers || []);
        } catch (error) {
            console.error('Error loading data:', error);
            toast.error('Erreur lors du chargement des donnees');
        } finally { setLoading(false); }
    };

    const loadImportHistory = async () => {
        try { const res = await csvAPI.getSupplierImportHistory(); setImportHistory(res.data.imports || []); }
        catch { setImportHistory([]); }
    };

    const handleClearImportHistory = async () => {
        if (!window.confirm('Effacer tout l\'historique des imports fournisseur ?')) return;
        try { await csvAPI.clearSupplierImportHistory(); toast.success('Historique efface'); setImportHistory([]); }
        catch { toast.error('Erreur lors de la suppression'); }
    };

    const handleBulkAction = async (action) => {
        const ids = Array.from(selectedIds);
        if (action === 'delete' && !window.confirm(`Supprimer ${ids.length} produit(s) ?`)) return;
        try { const res = await productsAPI.bulkAction(ids, action); toast.success(res.data.message); deselectAll(); loadData(); }
        catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    };

    const handleToggleReadyToOrder = async (productId) => {
        try {
            const product = products.find(p => p.id === productId);
            await productsAPI.update(productId, { ready_to_order: !product?.ready_to_order });
            toast.success(!product?.ready_to_order ? 'Produit pret a commander' : 'Produit en mode demande de prix');
            loadData();
        } catch { toast.error('Erreur lors de la mise a jour'); }
    };

    const handleToggleActive = async (productId) => {
        try { const res = await productsAPI.toggleActive(productId); toast.success(res.data.is_active ? 'Produit active' : 'Produit desactive'); loadData(); }
        catch { toast.error('Erreur lors de la mise a jour'); }
    };

    const handleToggleWeeklyOffer = async (productId) => {
        try { const res = await productsAPI.toggleWeeklyOffer(productId); toast.success(res.data.is_weekly_offer ? 'Ajoute aux offres de la semaine' : 'Retire des offres de la semaine'); loadData(); }
        catch (error) { toast.error(error.response?.data?.detail || 'Erreur lors de la mise a jour'); }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name || !formData.price || !formData.category_id) {
            toast.error('Veuillez remplir tous les champs obligatoires');
            return;
        }
        try {
            const validPrices = formData.prices_by_category.filter(p => p.price && parseFloat(p.price) > 0);
            const data = {
                ...formData, price: parseFloat(formData.price), min_order: parseInt(formData.min_order) || 1,
                stock: parseInt(formData.stock) || 100, product_code: formData.product_code || null,
                gen_code: formData.gen_code || null, package_weight: formData.package_weight ? parseFloat(formData.package_weight) : 0,
                purchase_price: formData.purchase_price ? parseFloat(formData.purchase_price) : null,
                calibre: formData.calibre || null,
                prices_by_category: validPrices.map(p => ({ category_id: p.category_id, price: parseFloat(p.price) })),
                season_start: formData.season_start ? parseInt(formData.season_start) : null,
                season_end: formData.season_end ? parseInt(formData.season_end) : null,
            };
            if (editingProduct) { await productsAPI.update(editingProduct.id, data); toast.success('Produit mis a jour'); }
            else { await productsAPI.create(data); toast.success('Produit cree'); }
            setIsDialogOpen(false); resetForm(); loadData();
        } catch (error) { toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement'); }
    };

    const handleEdit = (product) => {
        setEditingProduct(product);
        const pricesByCat = clientCategories.map(cat => {
            const existingPrice = product.prices_by_category?.find(p => p.category_id === cat.id);
            return { category_id: cat.id, price: existingPrice ? existingPrice.price.toString() : '' };
        });
        setFormData({
            name: product.name, description: product.description, price: product.price.toString(),
            unit: product.unit, min_order: product.min_order, category_id: product.category_id,
            image_url: product.image_url, origin: product.origin || '', stock: product.stock,
            product_code: product.product_code || '', gen_code: product.gen_code || '',
            package_weight: product.package_weight ? product.package_weight.toString() : '',
            purchase_price: product.purchase_price?.toString() || '', calibre: product.calibre || '',
            prices_by_category: pricesByCat, is_weekly_offer: product.is_weekly_offer || false,
            season_start: product.season_start || '', season_end: product.season_end || '',
        });
        setShowPricing(product.prices_by_category?.length > 0);
        setProductSuppliers(product.suppliers || []);
        setIsDialogOpen(true);
    };

    const handleDeleteClick = (product) => { setProductToDelete(product); setIsDeleteDialogOpen(true); };
    const handleDeleteConfirm = async () => {
        if (!productToDelete) return;
        try { await productsAPI.delete(productToDelete.id); toast.success('Produit supprime'); loadData(); }
        catch { toast.error('Erreur lors de la suppression'); }
        finally { setIsDeleteDialogOpen(false); setProductToDelete(null); }
    };

    const resetForm = () => {
        setEditingProduct(null); setShowPricing(false); setProductSuppliers([]);
        setFormData({
            name: '', description: '', price: '', unit: 'kg', min_order: 1, category_id: '',
            image_url: '', origin: '', stock: 100, product_code: '', gen_code: '',
            package_weight: '', calibre: '',
            prices_by_category: clientCategories.map(cat => ({ category_id: cat.id, price: '' })),
            is_weekly_offer: false, season_start: '', season_end: '',
        });
    };

    const handleExportCSV = async () => {
        toast.info('Preparation du telechargement...');
        try {
            const token = localStorage.getItem('prixdeal_token');
            const result = await csvAPI.downloadProducts(token);
            if (result.success) toast.success('Fichier telecharge !');
            else toast.error(`Erreur: ${result.error || 'Telechargement echoue'}`);
        } catch (error) { console.error('Export error:', error); toast.error('Erreur lors du telechargement.'); }
    };

    const handlePrintList = () => {
        const filters = {};
        if (categoryFilter !== 'all') { const cat = categories.find(c => c.id === categoryFilter); filters.category = cat ? cat.name : categoryFilter; }
        if (statusFilter !== 'all') { const labels = { active: 'Actifs', inactive: 'Masques', weekly: 'Offres semaine', orderable: 'Prets a commander', not_orderable: 'Non prets a commander' }; filters.status = labels[statusFilter] || statusFilter; }
        if (search) filters.search = search;
        const success = printContent('Liste des Produits', generateProductsListHTML(products, categories, filters));
        if (success) toast.success('Fenetre d\'impression ouverte.');
        else toast.error('Fenetre d\'impression bloquee.');
    };

    const handleImportCSV = async (e) => {
        const file = e.target.files[0]; if (!file) return;
        const fd = new FormData(); fd.append('file', file);
        try {
            const res = await csvAPI.importProducts(fd);
            toast.success(`Import termine: ${res.data.imported} crees, ${res.data.updated} mis a jour`);
            if (res.data.errors?.length > 0) toast.warning(`${res.data.errors.length} erreur(s)`);
            loadData();
        } catch (error) { toast.error(error.response?.data?.detail || 'Erreur lors de l\'import'); }
        e.target.value = '';
    };

    const handleImportSupplier = async (e) => {
        const file = e.target.files[0]; if (!file) return;
        const fd = new FormData(); fd.append('file', file);
        try {
            toast.info('Import fournisseur en cours...');
            const res = await csvAPI.importSupplierProducts(fd);
            toast.success(res.data.message);
            if (res.data.errors?.length > 0) toast.warning(`${res.data.errors.length} erreur(s)`);
            loadData();
            if (isSuperAdmin) loadImportHistory();
        } catch (error) { toast.error(error.response?.data?.detail || 'Erreur lors de l\'import fournisseur'); }
        e.target.value = '';
    };

    const handleDownloadSupplierTemplate = async () => {
        try {
            toast.info('Preparation du modele...');
            const res = await csvAPI.downloadSupplierTemplate();
            const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a'); link.href = url; link.download = 'modele_fournisseur_prixdeal.csv';
            document.body.appendChild(link); link.click(); document.body.removeChild(link); window.URL.revokeObjectURL(url);
            toast.success('Modele telecharge !');
        } catch { toast.error('Erreur lors du telechargement du modele'); }
    };

    const handleExportSelectedExcel = () => {
        const selected = products.filter(p => selectedIds.has(p.id));
        if (selected.length === 0) { toast.error('Selectionnez au moins un produit'); return; }
        import('xlsx-js-style').then(XLSX => {
            const data = selected.map(p => ({ 'DESIGNATION PRODUIT': p.name || '', 'GENCODE': p.gen_code || '', 'PRIX HT': p.price != null ? Number(p.price.toFixed(2)) : '', 'PVC': '' }));
            const ws = XLSX.utils.json_to_sheet(data);
            ws['!cols'] = [{ wch: Math.max(28, ...selected.map(p => (p.name || '').length + 2)) }, { wch: 16 }, { wch: 12 }, { wch: 12 }];
            const range = XLSX.utils.decode_range(ws['!ref']);
            const border = { top: { style: 'thin', color: { rgb: '000000' } }, bottom: { style: 'thin', color: { rgb: '000000' } }, left: { style: 'thin', color: { rgb: '000000' } }, right: { style: 'thin', color: { rgb: '000000' } } };
            for (let r = range.s.r; r <= range.e.r; r++) {
                for (let c = range.s.c; c <= range.e.c; c++) {
                    const addr = XLSX.utils.encode_cell({ r, c });
                    if (!ws[addr]) ws[addr] = { v: '', t: 's' };
                    if (!ws[addr].s) ws[addr].s = {};
                    ws[addr].s.border = border;
                    ws[addr].s.alignment = { horizontal: (c === 0 && r > 0) ? 'left' : 'center', vertical: 'center' };
                    if (r === 0) { ws[addr].s.font = { bold: true, sz: 11 }; ws[addr].s.fill = { fgColor: { rgb: 'E8F5E9' } }; }
                }
            }
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Produits');
            XLSX.writeFile(wb, `produits_selection_${selected.length}.xlsx`);
            toast.success(`${selected.length} produit(s) exportes en Excel`);
        });
    };

    const totalPages = Math.ceil(totalProducts / PRODUCTS_PER_PAGE);
    const weeklyOffersCount = products.filter(p => p.is_weekly_offer).length;
    const activeCount = products.filter(p => p.is_active).length;
    const inactiveCount = products.filter(p => !p.is_active).length;

    if (loading) {
        return (<div className="min-h-screen bg-sand flex items-center justify-center"><div className="animate-pulse text-muted-foreground">Chargement...</div></div>);
    }

    return (
        <div className="min-h-screen bg-sand bg-botanical" data-testid="admin-products-page">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-leaf/10 flex items-center justify-center">
                            <Leaf className="w-6 h-6 text-leaf" />
                        </div>
                        <div>
                            <h1 className="font-heading text-3xl font-bold tracking-tight text-jungle">Gestion des produits</h1>
                            <div className="flex gap-3 text-sm text-muted-foreground">
                                <span>{activeCount} actifs</span><span>|</span>
                                <span>{inactiveCount} masques</span><span>|</span>
                                <span className="text-amber-600">{weeklyOffersCount} offres semaine</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button onClick={handleExportSelectedExcel} variant="outline" className="border-emerald-500 text-emerald-600 hover:bg-emerald-50 rounded-xl" disabled={selectedCount === 0} data-testid="export-selected-excel-button">
                            <FileSpreadsheet className="h-4 w-4 mr-2" />Excel ({selectedCount} selectionnes)
                        </Button>
                        <Button onClick={handlePrintList} variant="outline" className="border-jungle/20 text-jungle rounded-xl" data-testid="print-products-button">
                            <Printer className="h-4 w-4 mr-2" />Imprimer
                        </Button>
                        <Button onClick={handleExportCSV} variant="outline" className="border-jungle/20 text-jungle rounded-xl" data-testid="export-products-button">
                            <Download className="h-4 w-4 mr-2" />Exporter CSV
                        </Button>
                        <Button onClick={() => fileInputRef.current?.click()} variant="outline" className="border-jungle/20 text-jungle rounded-xl" data-testid="import-products-button">
                            <Upload className="h-4 w-4 mr-2" />Importer CSV
                        </Button>
                        <input type="file" ref={fileInputRef} accept=".csv" onChange={handleImportCSV} className="hidden" />
                        <Button onClick={() => supplierFileRef.current?.click()} className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl" data-testid="import-supplier-button">
                            <PackageOpen className="h-4 w-4 mr-2" />Import Fournisseur
                        </Button>
                        <input type="file" ref={supplierFileRef} accept=".csv" onChange={handleImportSupplier} className="hidden" />
                        <Button onClick={handleDownloadSupplierTemplate} variant="outline" className="border-orange-300 text-orange-600 hover:bg-orange-50 rounded-xl" data-testid="download-supplier-template-button">
                            <Download className="h-4 w-4 mr-2" />Modele Fournisseur
                        </Button>
                        <Button onClick={() => setPricePdfOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl" data-testid="open-price-import-pdf-button">
                            <FileText className="h-4 w-4 mr-2" />Importer Tarifs PDF
                        </Button>
                        <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} className="bg-jungle hover:bg-jungle/90 text-sand rounded-xl" data-testid="add-product-button">
                            <Plus className="h-4 w-4 mr-2" />Ajouter un produit
                        </Button>
                    </div>
                </div>

                <ProductFilters search={search} setSearch={setSearch} categoryFilter={categoryFilter}
                    setCategoryFilter={setCategoryFilter} statusFilter={statusFilter} setStatusFilter={setStatusFilter}
                    categories={categories} setCurrentPage={setCurrentPage} />

                {isSuperAdmin && (
                    <ProductImportHistory importHistory={importHistory} showImportHistory={showImportHistory}
                        setShowImportHistory={setShowImportHistory} loadImportHistory={loadImportHistory}
                        handleClearImportHistory={handleClearImportHistory} />
                )}

                {/* Products Table */}
                <Card className="border-0 bg-white/80 backdrop-blur-sm shadow-sm rounded-2xl">
                    <CardHeader>
                        <CardTitle className="font-heading text-jungle">
                            {totalProducts} produit{totalProducts !== 1 ? 's' : ''} trouve{totalProducts !== 1 ? 's' : ''}
                            {searchQuery && <span className="text-sm font-normal text-muted-foreground ml-2">pour "{searchQuery}"</span>}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <ProductBulkActions selectedCount={selectedCount} handleBulkAction={handleBulkAction} deselectAll={deselectAll} />
                        <ProductTable products={products} categories={categories} isSelected={isSelected}
                            toggleItem={toggleItem} toggleAll={toggleAll} allSelected={allSelected}
                            handleToggleActive={handleToggleActive} handleToggleWeeklyOffer={handleToggleWeeklyOffer}
                            handleToggleReadyToOrder={handleToggleReadyToOrder} handleEdit={handleEdit} handleDeleteClick={handleDeleteClick} />
                    </CardContent>
                    <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalProducts} setCurrentPage={setCurrentPage} label="produits" />
                </Card>
            </div>

            <ProductForm isOpen={isDialogOpen} setIsOpen={setIsDialogOpen} editingProduct={editingProduct}
                formData={formData} setFormData={setFormData} categories={categories} allSuppliers={allSuppliers}
                onSubmit={handleSubmit} onReset={resetForm} />

            <PriceImportPDF
                open={pricePdfOpen}
                onOpenChange={setPricePdfOpen}
                onSuccess={loadData}
                categories={categories}
            />

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <AlertDialogContent className="bg-sand">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-jungle">Confirmer la suppression</AlertDialogTitle>
                        <AlertDialogDescription>
                            Etes-vous sur de vouloir supprimer le produit "{productToDelete?.name}" ? Cette action est irreversible.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-xl">Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDeleteConfirm} className="bg-destructive hover:bg-destructive/90 rounded-xl">Supprimer</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};
